import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for TV/Remote-friendly keyboard navigation
 * Supports arrow keys, Enter/Space for selection, and gamepad input
 * 
 * @param {Object} options
 * @param {string} options.containerSelector - CSS selector for the navigation container
 * @param {string} options.itemSelector - CSS selector for navigable items (default: '[data-nav-item]')
 * @param {boolean} options.wrapAround - Whether to wrap navigation at edges (default: true)
 * @param {function} options.onSelect - Callback when item is selected (Enter/Space)
 * @param {function} options.onNavigate - Callback when focus changes
 * @param {boolean} options.enabled - Whether navigation is enabled (default: true)
 * @param {string} options.orientation - 'horizontal', 'vertical', or 'grid' (default: 'grid')
 * @param {number} options.columns - Number of columns for grid navigation (default: auto-detect)
 */
const useKeyboardNavigation = ({
  containerSelector,
  itemSelector = '[data-nav-item]',
  wrapAround = true,
  onSelect,
  onNavigate,
  enabled = true,
  orientation = 'grid',
  columns = null
} = {}) => {
  const containerRef = useRef(null);
  const currentIndexRef = useRef(0);

  const getNavigableItems = useCallback(() => {
    const container = containerRef.current || 
      (containerSelector ? document.querySelector(containerSelector) : null);
    if (!container) return [];
    return Array.from(container.querySelectorAll(itemSelector));
  }, [containerSelector, itemSelector]);

  const detectColumns = useCallback((items) => {
    if (columns) return columns;
    if (items.length < 2) return 1;
    
    // Auto-detect columns by comparing y positions
    const firstY = items[0]?.getBoundingClientRect().top;
    let cols = 1;
    for (let i = 1; i < items.length; i++) {
      if (items[i].getBoundingClientRect().top === firstY) {
        cols++;
      } else {
        break;
      }
    }
    return cols;
  }, [columns]);

  const focusItem = useCallback((index, items = null) => {
    const navigableItems = items || getNavigableItems();
    if (navigableItems.length === 0) return;

    // Normalize index
    let newIndex = index;
    if (wrapAround) {
      newIndex = ((index % navigableItems.length) + navigableItems.length) % navigableItems.length;
    } else {
      newIndex = Math.max(0, Math.min(index, navigableItems.length - 1));
    }

    const item = navigableItems[newIndex];
    if (item) {
      item.focus();
      currentIndexRef.current = newIndex;
      onNavigate?.(newIndex, item);
      
      // Scroll item into view for TV screens
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [getNavigableItems, wrapAround, onNavigate]);

  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    const items = getNavigableItems();
    if (items.length === 0) return;

    const cols = detectColumns(items);
    const currentIndex = currentIndexRef.current;
    let newIndex = currentIndex;
    let handled = false;

    switch (event.key) {
      case 'ArrowUp':
        if (orientation === 'vertical' || orientation === 'grid') {
          newIndex = currentIndex - cols;
          handled = true;
        }
        break;
      case 'ArrowDown':
        if (orientation === 'vertical' || orientation === 'grid') {
          newIndex = currentIndex + cols;
          handled = true;
        }
        break;
      case 'ArrowLeft':
        if (orientation === 'horizontal' || orientation === 'grid') {
          newIndex = currentIndex - 1;
          handled = true;
        }
        break;
      case 'ArrowRight':
        if (orientation === 'horizontal' || orientation === 'grid') {
          newIndex = currentIndex + 1;
          handled = true;
        }
        break;
      case 'Enter':
      case ' ':
        // Activate the currently focused item
        const focusedItem = items[currentIndex];
        if (focusedItem) {
          event.preventDefault();
          focusedItem.click();
          onSelect?.(currentIndex, focusedItem);
        }
        handled = true;
        break;
      case 'Home':
        newIndex = 0;
        handled = true;
        break;
      case 'End':
        newIndex = items.length - 1;
        handled = true;
        break;
      default:
        break;
    }

    if (handled) {
      event.preventDefault();
      if (newIndex !== currentIndex) {
        focusItem(newIndex, items);
      }
    }
  }, [enabled, getNavigableItems, detectColumns, orientation, focusItem, onSelect]);

  // Set up event listeners
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current || 
      (containerSelector ? document.querySelector(containerSelector) : document);
    
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [enabled, containerSelector, handleKeyDown]);

  // Gamepad support
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    let gamepadIndex = null;
    let lastButtons = {};
    let animationFrame = null;

    const handleGamepadConnected = (event) => {
      gamepadIndex = event.gamepad.index;
    };

    const handleGamepadDisconnected = () => {
      gamepadIndex = null;
    };

    const pollGamepad = () => {
      if (gamepadIndex === null) {
        animationFrame = requestAnimationFrame(pollGamepad);
        return;
      }

      const gamepad = navigator.getGamepads()[gamepadIndex];
      if (!gamepad) {
        animationFrame = requestAnimationFrame(pollGamepad);
        return;
      }

      const items = getNavigableItems();
      const cols = detectColumns(items);
      const currentIndex = currentIndexRef.current;

      // D-pad or left stick navigation
      const threshold = 0.5;
      const axes = gamepad.axes;
      const buttons = gamepad.buttons;

      // Check for new button presses (not held)
      const isNewPress = (buttonIndex) => {
        const pressed = buttons[buttonIndex]?.pressed;
        const wasPressed = lastButtons[buttonIndex];
        lastButtons[buttonIndex] = pressed;
        return pressed && !wasPressed;
      };

      // D-pad: buttons 12-15 (up, down, left, right)
      if (isNewPress(12)) focusItem(currentIndex - cols, items); // Up
      if (isNewPress(13)) focusItem(currentIndex + cols, items); // Down
      if (isNewPress(14)) focusItem(currentIndex - 1, items); // Left
      if (isNewPress(15)) focusItem(currentIndex + 1, items); // Right

      // A button (0) for select, B button (1) for back
      if (isNewPress(0)) {
        const focusedItem = items[currentIndex];
        if (focusedItem) {
          focusedItem.click();
          onSelect?.(currentIndex, focusedItem);
        }
      }

      // Left stick (axes 0, 1)
      if (Math.abs(axes[0]) > threshold || Math.abs(axes[1]) > threshold) {
        // Debounce stick input
        if (!lastButtons.stickMoved) {
          lastButtons.stickMoved = true;
          if (axes[1] < -threshold) focusItem(currentIndex - cols, items); // Up
          if (axes[1] > threshold) focusItem(currentIndex + cols, items); // Down
          if (axes[0] < -threshold) focusItem(currentIndex - 1, items); // Left
          if (axes[0] > threshold) focusItem(currentIndex + 1, items); // Right
          
          setTimeout(() => { lastButtons.stickMoved = false; }, 200);
        }
      }

      animationFrame = requestAnimationFrame(pollGamepad);
    };

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);
    animationFrame = requestAnimationFrame(pollGamepad);

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [enabled, getNavigableItems, detectColumns, focusItem, onSelect]);

  return {
    containerRef,
    focusItem,
    focusFirst: () => focusItem(0),
    focusLast: () => focusItem(getNavigableItems().length - 1),
    getCurrentIndex: () => currentIndexRef.current
  };
};

export default useKeyboardNavigation;
